<template>
    <div class="dropdown-menu user-menu p-0">
        <!-- Identity -->
        <div class="user-menu__header">
            <div class="user-avatar" :style="{ backgroundColor: avatarColor }">{{ initials }}</div>
            <div class="user-menu__identity">
                <div class="user-menu__name text-truncate" :title="user.name">{{ user.name }}</div>
                <div class="user-menu__email text-truncate" :title="user.email">{{ user.email }}</div>
                <span v-if="roleLabel" class="user-menu__role">{{ roleLabel }}</span>
            </div>
        </div>

        <!-- Session -->
        <div v-if="sessionText" class="user-menu__session">
            <i class="fe fe-clock me-2"></i>{{ sessionText }}
        </div>

        <!-- Links -->
        <!-- <div class="user-menu__section">
            <router-link to="/changelog" class="dropdown-item user-menu__item">
                <i class="fa fa-wand-magic-sparkles"></i> What's new
            </router-link>
            <a class="dropdown-item user-menu__item" href="https://help.aqfer.net" target="_blank" rel="noopener">
                <i class="fa fa-circle-info"></i> Knowledge Base
                <i class="fe fe-external-link user-menu__external"></i>
            </a>
            <a class="dropdown-item user-menu__item" href="mailto:support@aqfer.com?Subject=CX Portal Support">
                <i class="fa fa-envelope"></i> Contact support
            </a>
        </div> -->

        <div class="user-menu__section user-menu__section--last">
            <router-link to="/logout" class="dropdown-item user-menu__item user-menu__logout">
                <i class="fa fa-arrow-right-from-bracket"></i> Log out
            </router-link>
        </div>
    </div>
</template>

<script>
const ROLE_LABELS = {
    aqfer_admin: 'Aqfer Admin',
    aqfer_manager: 'Aqfer Manager',
    aqfer_user: 'Aqfer User',
    client_admin: 'Client Admin',
    client_user: 'Client User',
};

/**
 * Account menu opened from the sidebar avatar. Renders the `.dropdown-menu`
 * itself, so it drops into a Bootstrap dropdown/dropup next to its toggle;
 * alignment classes (e.g. dropdown-menu-end) pass through as attrs.
 */
export default {
    name: 'UserMenu',
    props: {
        avatarColor: { type: String, default: '' },
        initials: { type: String, default: '' },
    },
    data() {
        return { now: Date.now() };
    },
    computed: {
        user() {
            return this.$store.state.currentUser || {};
        },
        roleLabel() {
            const roles = this.user.roles;
            if (!roles || !roles.length) return '';
            return ROLE_LABELS[this.$store.getters.userRoles.getUserRole(roles)] || '';
        },
        sessionText() {
            const raw = this.$cookies.get('authExpireTime');
            const expiry = raw ? new Date(raw).getTime() : NaN;
            if (Number.isNaN(expiry) || expiry <= this.now) return '';
            const mins = Math.max(1, Math.round((expiry - this.now) / 60000));
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return `Session expires in ${h ? `${h}h ${m}m` : `${m}m`}`;
        },
    },
    mounted() {
        // Refresh the session countdown each time the menu opens.
        this.$el.parentElement.addEventListener('show.bs.dropdown', this.refresh);
    },
    beforeUnmount() {
        this.$el.parentElement.removeEventListener('show.bs.dropdown', this.refresh);
    },
    methods: {
        refresh() {
            this.now = Date.now();
        },
    },
};
</script>

<style scoped>
.user-menu {
    width: 14rem;
    overflow: hidden;
    border-radius: 0.625rem;
}

.user-menu__header {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.75rem;
    background: #f5f8fc;
    border-bottom: 1px solid #e3ebf6;
}

.user-avatar {
  /* Center the content */
  align-items: center;
  display: flex;
  justify-content: center;
  text-transform: uppercase;
  font-size: 20px;

  /* Colors */
  color: #000;

  /* Rounded border */
  border-radius: 50%;
  height: 2.5rem;
  width: 2.5rem;
}

.user-menu__identity {
    min-width: 0;
}

.user-menu__name {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1.3;
}

.user-menu__email {
    font-size: 0.75rem;
    color: #6e84a3;
}

.user-menu__role {
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.0625rem 0.4375rem;
    border-radius: 999px;
    background: rgba(44, 123, 229, 0.12);
    color: #2c7be5;
    font-size: 0.625rem;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.user-menu__session {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.75rem;
    font-size: 0.6875rem;
    color: #6e84a3;
    border-bottom: 1px solid #e3ebf6;
}

.user-menu__section {
    padding: 0.25rem 0;
    border-bottom: 1px solid #e3ebf6;
}

.user-menu__section--last {
    border-bottom: 0;
}

.user-menu__item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.4375rem 0.75rem;
    font-size: 0.8125rem;
}

.user-menu__item > i:first-child {
    width: 1rem;
    text-align: center;
    color: #95aac9;
}

.user-menu__external {
    margin-left: auto;
    font-size: 0.6875rem;
    color: #b1c2d9;
}

.user-menu__logout,
.user-menu__logout > i:first-child {
    color: #e63757;
}

.user-menu__logout:hover,
.user-menu__logout:focus {
    background: rgba(230, 55, 87, 0.08);
    color: #e63757;
}
</style>

<!-- Unscoped: scoped `:global(body.dark-mode) .x` collapses to just `body.dark-mode`. -->
<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    .user-menu__header {
        background: $dark-elevated-bg;
        border-color: $dark-border-color;
    }

    .user-menu__name {
        color: $dark-text-primary;
    }

    .user-menu__email,
    .user-menu__session {
        color: $dark-text-secondary;
    }

    .user-menu__session,
    .user-menu__section {
        border-color: $dark-border-color;
    }

    .user-menu__role {
        background: rgba(44, 123, 229, 0.2);
        color: #7fb0f0;
    }

    .user-menu__item > i:first-child,
    .user-menu__external {
        color: $dark-text-muted;
    }

    .user-menu__logout,
    .user-menu__logout > i:first-child {
        color: #f0677f;
    }

    .user-menu__logout:hover,
    .user-menu__logout:focus {
        background: rgba(230, 55, 87, 0.15);
        color: #f0677f;
    }
}
</style>
